// Link de pagamento da Belluno, como a tela lê o `belluno_link` do pedido (o resumo que o servidor grava
// em services/belluno_service.resumo_transacao). As regras (quem pode gerar, cobrança dupla, estorno)
// moram no servidor; aqui só o que a tela precisa para mostrar e decidir o que oferecer.
import { diaDoCalendario } from './format';

// O link do pedido inteiro ou, com `parte` (o índice), o da parte Belluno do pagamento dividido.
const parteDo = (pedido, parte) => (pedido?.pagamento_split || []).find((p) => p.indice === parte);
export const linkBelluno = (pedido, parte = null) =>
  (parte == null ? pedido?.belluno_link : parteDo(pedido, parte)?.belluno_link) || null;

// Link que o cliente ainda pode pagar.
export const linkBellunoAtivo = (pedido, parte = null) => {
  const link = linkBelluno(pedido, parte);
  return Boolean(link?.aberto && link?.transaction_id);
};

export const linkBellunoPago = (pedido, parte = null) => Boolean(linkBelluno(pedido, parte)?.pago);

// Os links que a Gestão e o Financeiro mostram: os das partes Belluno do pagamento dividido ou, sem split, o do pedido
// inteiro (um link do pedido inteiro trocado pelo split fica só no histórico).
export const linksDoPedido = (pedido) => {
  const partes = pedido?.pagamento_split || [];
  if (partes.length) {
    return partes.filter((p) => p.belluno_link?.transaction_id)
      .map((p) => ({ parte: p.indice, titulo: `Link Belluno do pagamento ${p.indice}` }));
  }
  return pedido?.belluno_link?.transaction_id ? [{ parte: null, titulo: 'Link de pagamento (Belluno)' }] : [];
};

// Pedido em Belluno que ainda dá para cobrar: não pago, não cancelado, sem comprovante em análise. Com link aberto
// (do pedido ou de uma parte) o vendedor só troca a forma; o servidor cancela o link antes.
export const podeCobrarBelluno = (pedido) =>
  pedido?.forma_pagamento === 'belluno'
  && pedido?.payment_status !== 'confirmed' && pedido?.payment_status !== 'refunded'
  && !['cancelado', 'comprovante_em_analise'].includes(pedido?.workflow_status);

// O que as rotas da Belluno devolvem, aplicado a uma cópia do pedido: o link do pedido inteiro ou, com `parte`, o
// da parte; o split e os estados quando vêm (sincronizar devolve tudo, o link do pedido inteiro desfaz o split).
export const comRespostaBelluno = (pedido, d) => {
  const novo = { ...pedido };
  if (d.pagamento_split !== undefined) novo.pagamento_split = d.pagamento_split;
  if (d.parte != null) {
    novo.pagamento_split = (novo.pagamento_split || []).map((p) => (p.indice === d.parte ? { ...p, belluno_link: d.belluno_link } : p));
  } else if (d.belluno_link !== undefined) {
    novo.belluno_link = d.belluno_link;
  }
  if (d.payment_status) novo.payment_status = d.payment_status;
  if (d.workflow_status) novo.workflow_status = d.workflow_status;
  if (d.valor_estornado != null) novo.valor_estornado = d.valor_estornado;
  return novo;
};

// "vence em 3 dias" / "vence hoje" / "venceu há 2 dias"; vazio sem data.
export const textoVencimento = (link, hoje = new Date()) => {
  if (!link?.valido_ate) return '';
  const dias = Math.round((diaDoCalendario(new Date(link.valido_ate)) - diaDoCalendario(hoje)) / 86400000);
  if (Number.isNaN(dias)) return '';
  if (dias > 1) return `vence em ${dias} dias`;
  if (dias === 1) return 'vence amanhã';
  if (dias === 0) return 'vence hoje';
  return dias === -1 ? 'venceu há 1 dia' : `venceu há ${-dias} dias`;
};

// "cartão Visa em 3x" / "PIX": igual à frase do histórico que o servidor grava.
export const descricaoPagamentoBelluno = (link) => {
  if (!link?.pago) return '';
  if (link.metodo === 'pix') return 'PIX';
  if (link.metodo === 'cartao') {
    const bandeira = link.bandeira ? ` ${String(link.bandeira).charAt(0).toUpperCase()}${String(link.bandeira).slice(1).toLowerCase()}` : '';
    const parcelas = link.parcelas > 1 ? ` em ${link.parcelas}x` : '';
    return `cartão${bandeira}${parcelas}`;
  }
  return 'link de pagamento';
};

// Tom do ponto de status: pago = ok, aberto = andando, encerrado = cinza (recusado = vermelho).
export const tomDoLink = (link) => {
  if (!link) return 'off';
  if (link.pago) return 'ok';
  if (link.aberto) return 'run';
  return String(link.status || '').toLowerCase() === 'refused' ? 'bad' : 'off';
};

// Quanto ainda dá para estornar do pagamento feito pela Belluno (teto de UI; o servidor confere). Numa parte, o saldo
// é o do link dela: o `valor_estornado` do pedido soma também os outros pagamentos.
export const restanteBelluno = (pedido, parte = null) => {
  const link = linkBelluno(pedido, parte);
  if (!link?.pago) return 0;
  if (parte != null) {
    const pagoParte = Number(link.valor) || Number(parteDo(pedido, parte)?.valor) || 0;
    return Math.max(0, pagoParte - (Number(link.estornado) || 0));
  }
  const pago = Number(link.valor) || Number(pedido?.total) || 0;
  return Math.max(0, pago - (Number(pedido?.valor_estornado) || 0));
};

export const MOTIVOS_ESTORNO_BELLUNO = [
  { value: 2, label: 'Cancelamento da compra' },
  { value: 1, label: 'Devolução do produto' },
  { value: 3, label: 'Preço incorreto' },
  { value: 4, label: 'Cobrança duplicada' },
];
